import React, { useState, useRef, useEffect } from 'react';
import {
    View, Text, StyleSheet, TouchableOpacity, Modal,
    Animated, PanResponder, Dimensions, Alert, Platform, StatusBar,
    ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Video } from 'expo-av';
import * as ImagePicker from 'expo-image-picker';
import { CameraView, useCameraPermissions, useMicrophonePermissions } from 'expo-camera';

const { width: SW } = Dimensions.get('window');

const MAX_DURATION = 30; // seconds
const TIMELINE_W = SW - 48;
const RING_SIZE = 72;
const RING_STROKE = 5;

// ── Duration ring (pure Animated, no SVG dependency) ────────────────────────
const DurationRing = ({ duration, max, color, isPercentage = false }) => {
    const anim = useRef(new Animated.Value(0)).current;
    useEffect(() => {
        Animated.spring(anim, {
            toValue: Math.min(duration / max, 1),
            useNativeDriver: false,
            bounciness: 4,
        }).start();
    }, [duration, max]);

    const borderWidth = RING_STROKE;
    return (
        <View style={[ring.wrap, { width: RING_SIZE, height: RING_SIZE }]}>
            {/* Track */}
            <View style={[ring.track, { borderColor: 'rgba(255,255,255,0.1)', borderWidth }]} />
            {/* Fill — rotated quarter-circle trick */}
            <Animated.View
                style={[ring.fill, {
                    borderColor: color,
                    borderWidth,
                    transform: [{
                        rotate: anim.interpolate({
                            inputRange: [0, 1],
                            outputRange: ['0deg', '360deg'],
                        }),
                    }],
                }]}
            />
            <View style={ring.center}>
                <Text style={[ring.text, { color }]}>
                    {isPercentage ? `${Math.round(duration)}%` : fmtDuration(duration)}
                </Text>
            </View>
        </View>
    );
};

const ring = StyleSheet.create({
    wrap: { position: 'relative', alignItems: 'center', justifyContent: 'center' },
    track: {
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        borderRadius: RING_SIZE / 2,
    },
    fill: {
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        borderRadius: RING_SIZE / 2,
        borderTopColor: 'transparent',
        borderRightColor: 'transparent',
    },
    center: {
        position: 'absolute',
        alignItems: 'center', justifyContent: 'center',
        width: RING_SIZE - RING_STROKE * 2,
        height: RING_SIZE - RING_STROKE * 2,
    },
    text: { fontSize: 12, fontWeight: '800', letterSpacing: 0.3 },
});

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmtDuration = (s) => {
    if (!s && s !== 0) return '—';
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const fmtSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const ringColor = (dur) => {
    if (!dur) return '#10b981';
    if (dur <= 20) return '#10b981';
    if (dur <= 27) return '#f59e0b';
    return '#ef4444';
};

// ── Main Component ───────────────────────────────────────────────────────────
const ShortClipPicker = ({ visible, onClose, onClipSelected }) => {
    const insets = useSafeAreaInsets();
    const [step, setStep] = useState('choose'); // 'choose' | 'preview' | 'record'
    const [clip, setClip] = useState(null);
    const [playing, setPlaying] = useState(true);
    const [picking, setPicking] = useState(false);
    const [recording, setRecording] = useState(false);
    const [recordTime, setRecordTime] = useState(0);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadComplete, setUploadComplete] = useState(false);
    const cameraRef = useRef(null);
    const [permission, requestPermission] = useCameraPermissions();
    const [micPermission, requestMicPermission] = useMicrophonePermissions();
    const timerRef = useRef(null);
    const uploadTimerRef = useRef(null);

    // Trim handle animations
    const startX = useRef(new Animated.Value(0)).current;
    const endX = useRef(new Animated.Value(TIMELINE_W)).current;

    const reset = () => {
        setStep('choose');
        setClip(null);
        setPlaying(true);
        setPicking(false);
        setUploading(false);
        setUploadProgress(0);
        setUploadComplete(false);
        startX.setValue(0);
        endX.setValue(TIMELINE_W);
        if (uploadTimerRef.current) clearInterval(uploadTimerRef.current);
    };

    const handleClose = () => { reset(); onClose(); };

    const pickGallery = async () => {
        if (picking) return;
        try {
            const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
            if (status !== 'granted') {
                Alert.alert('Permission Required', 'Please grant media library access to pick a clip.');
                return;
            }
            setPicking(true);
            const result = await ImagePicker.launchImageLibraryAsync({
                mediaTypes: ImagePicker.MediaTypeOptions.Videos,
                allowsEditing: false,
                quality: 0.85,
                videoMaxDuration: 120, // allow longer — server will trim to 30s
                selectionLimit: 1,
            });
            if (!result.canceled && result.assets?.[0]) {
                const asset = result.assets[0];
                const durSec = asset.duration ? asset.duration / 1000 : null;
                setClip({
                    uri: asset.uri,
                    duration: durSec,
                    fileName: asset.fileName || `clip_${Date.now()}.mp4`,
                    type: 'video',
                    fileSize: asset.fileSize,
                    width: asset.width,
                    height: asset.height,
                });
                endX.setValue(TIMELINE_W);
                setStep('preview');
            }
        } catch (e) {
            console.error('[ShortClipPicker] pickGallery error:', e);
            Alert.alert('Error', 'Failed to open gallery. Please try again.');
        } finally {
            setPicking(false);
        }
    };

    const startRecording = async () => {
        // Request Camera Permission
        if (!permission?.granted) {
            const { granted } = await requestPermission();
            if (!granted) {
                Alert.alert('Permission Denied', 'Camera access is required to record a clip.');
                return;
            }
        }
        // Request Microphone Permission
        if (!micPermission?.granted) {
            const { granted } = await requestMicPermission();
            if (!granted) {
                Alert.alert('Permission Denied', 'Microphone access is required for video recording.');
                return;
            }
        }
        setStep('record');
    };

    const handleRecordPress = async () => {
        if (recording) {
            stopRecording();
            return;
        }

        if (cameraRef.current) {
            try {
                setRecording(true);
                setRecordTime(0);
                
                timerRef.current = setInterval(() => {
                    setRecordTime(prev => {
                        if (prev >= MAX_DURATION - 1) {
                            stopRecording();
                            return MAX_DURATION;
                        }
                        return prev + 1;
                    });
                }, 1000);

                const result = await cameraRef.current.recordAsync({
                    maxDuration: MAX_DURATION,
                });

                if (result) {
                    setClip({
                        uri: result.uri,
                        duration: recordTime || MAX_DURATION,
                        fileName: `recorded_clip_${Date.now()}.mp4`,
                        type: 'video',
                    });
                    setStep('preview');
                }
            } catch (err) {
                console.error('[ShortClipPicker] Recording error:', err);
                Alert.alert('Error', 'Failed to start recording.');
                stopRecording();
            }
        }
    };

    const stopRecording = () => {
        if (timerRef.current) clearInterval(timerRef.current);
        setRecording(false);
        if (cameraRef.current) {
            cameraRef.current.stopRecording();
        }
    };

    const useClip = () => {
        if (!clip) return;
        setUploading(true);
        setUploadProgress(0);

        // Simulate upload progress
        uploadTimerRef.current = setInterval(() => {
            setUploadProgress(prev => {
                if (prev >= 1) {
                    clearInterval(uploadTimerRef.current);
                    setUploading(false);
                    setUploadComplete(true);
                    return 1;
                }
                return prev + 0.05;
            });
        }, 150);
    };

    const finalize = () => {
        onClipSelected(clip);
        reset();
        onClose();
    };

    const isOver = clip?.duration && clip.duration > MAX_DURATION;
    const color = ringColor(clip?.duration);

    // Trim handles PanResponder
    const startPan = useRef(PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onPanResponderMove: (_, gs) => {
            const cur = endX.__getValue() - 40;
            startX.setValue(Math.max(0, Math.min(gs.moveX - 24, cur)));
        },
    })).current;

    const endPan = useRef(PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onPanResponderMove: (_, gs) => {
            const cur = startX.__getValue() + 40;
            endX.setValue(Math.min(TIMELINE_W, Math.max(gs.moveX - 24, cur)));
        },
    })).current;

    if (!visible) return null;

    return (
        <Modal
            visible={visible}
            transparent={false}
            animationType="slide"
            statusBarTranslucent
            onRequestClose={handleClose}
        >
            <View style={s.root}>
                <StatusBar barStyle="light-content" backgroundColor="#000" />
                <LinearGradient colors={['#07000f', '#0d0020', '#000']} style={StyleSheet.absoluteFill} />

                {/* ── STEP 1: Choose source ── */}
                {step === 'choose' && (
                    <View style={[s.page, { paddingTop: insets.top + 16, paddingBottom: insets.bottom + 24 }]}>
                        {/* Header */}
                        <View style={s.hdr}>
                            <TouchableOpacity style={s.closeBtn} onPress={handleClose} activeOpacity={0.7}>
                                <Ionicons name="close" size={20} color="#fff" />
                            </TouchableOpacity>
                            <Text style={s.hdrTitle}>Add a Short Clip</Text>
                            <View style={{ width: 40 }} />
                        </View>

                        {/* Limit badge */}
                        <View style={s.limitPill}>
                            <Ionicons name="timer-outline" size={13} color="#f59e0b" />
                            <Text style={s.limitPillText}>
                                Clips trimmed to <Text style={{ color: '#fbbf24', fontWeight: '900' }}>30 seconds</Text> · compressed for mobile
                            </Text>
                        </View>

                        {/* Cards */}
                        <View style={s.cardsWrap}>
                            {/* Gallery card */}
                            <TouchableOpacity
                                style={s.card}
                                onPress={pickGallery}
                                activeOpacity={0.8}
                                disabled={picking}
                            >
                                <LinearGradient
                                    colors={['rgba(124,58,237,0.25)', 'rgba(91,33,182,0.12)', 'rgba(0,0,0,0)']}
                                    style={s.cardGrad}
                                >
                                    <View style={[s.cardIconOrb, { backgroundColor: 'rgba(139,92,246,0.18)' }]}>
                                        {picking
                                            ? <Ionicons name="hourglass" size={38} color="#a78bfa" />
                                            : <Ionicons name="film-outline" size={38} color="#a78bfa" />
                                        }
                                    </View>
                                    <Text style={s.cardTitle}>Choose from Gallery</Text>
                                    <Text style={s.cardSub}>Pick a video from your photo library</Text>

                                    <LinearGradient
                                        colors={['#7c3aed', '#5b21b6']}
                                        style={s.cardBtn}
                                        start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
                                    >
                                        <Ionicons name="folder-open-outline" size={16} color="#fff" />
                                        <Text style={s.cardBtnText}>{picking ? 'Opening…' : 'Browse Gallery'}</Text>
                                    </LinearGradient>
                                </LinearGradient>
                            </TouchableOpacity>

                            {/* Record card */}
                            <TouchableOpacity
                                style={[s.card, { marginTop: 16 }]}
                                activeOpacity={0.8}
                                onPress={startRecording}
                            >
                                <LinearGradient
                                    colors={['rgba(239,68,68,0.25)', 'rgba(239,68,68,0.12)', 'rgba(0,0,0,0)']}
                                    style={s.cardGrad}
                                >
                                    <View style={[s.cardIconOrb, { backgroundColor: 'rgba(239,68,68,0.18)' }]}>
                                        <Ionicons name="radio-button-on-outline" size={38} color="#f87171" />
                                    </View>
                                    <Text style={[s.cardTitle, { color: '#fca5a5' }]}>Record Now</Text>
                                    <Text style={s.cardSub}>Capture up to 30s directly from your camera</Text>
                                    
                                    <LinearGradient
                                        colors={['#ef4444', '#b91c1c']}
                                        style={s.cardBtn}
                                        start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
                                    >
                                        <Ionicons name="videocam-outline" size={16} color="#fff" />
                                        <Text style={s.cardBtnText}>Open Camera</Text>
                                    </LinearGradient>
                                </LinearGradient>
                            </TouchableOpacity>
                        </View>
                    </View>
                )}

                {/* ── STEP 1.5: Record ── */}
                {step === 'record' && (
                    <View style={s.cameraPage}>
                        <CameraView
                            ref={cameraRef}
                            style={StyleSheet.absoluteFill}
                            mode="video"
                            facing="front"
                        >
                            <View style={[s.cameraOverlay, { paddingTop: insets.top + 16, paddingBottom: insets.bottom + 40 }]}>
                                {/* Header */}
                                <View style={s.hdr}>
                                    <TouchableOpacity style={s.closeBtn} onPress={() => setStep('choose')}>
                                        <Ionicons name="chevron-back" size={20} color="#fff" />
                                    </TouchableOpacity>
                                    <Text style={s.hdrTitle}>Recording Clip</Text>
                                    <View style={{ width: 40 }} />
                                </View>

                                {/* Center ring / countdown */}
                                <View style={s.recordRingWrap}>
                                    <DurationRing duration={recordTime} max={MAX_DURATION} color={recording ? '#ef4444' : '#fff'} />
                                </View>

                                {/* Bottom controls */}
                                <View style={s.recordCtrls}>
                                    <TouchableOpacity 
                                        style={[s.recordBtn, recording && s.recordBtnActive]} 
                                        onPress={handleRecordPress}
                                        activeOpacity={0.9}
                                    >
                                        <View style={[s.recordBtnInner, recording && s.recordBtnInnerActive]} />
                                    </TouchableOpacity>
                                    <Text style={s.recordStatusText}>
                                        {recording ? 'Tap to Stop' : 'Tap to Start'}
                                    </Text>
                                </View>
                            </View>
                        </CameraView>
                    </View>
                )}

                {/* ── STEP 2: Preview ── */}
                {step === 'preview' && clip && (
                    <View style={s.previewRoot}>
                        {/* Video fills top portion */}
                        <TouchableOpacity
                            activeOpacity={1}
                            style={s.videoWrap}
                            onPress={() => setPlaying(p => !p)}
                        >
                            <Video
                                source={{ uri: clip.uri }}
                                style={s.video}
                                resizeMode="cover"
                                shouldPlay={playing}
                                isLooping
                                isMuted={false}
                                volume={1.0}
                            />

                            {/* Gradient scrim at bottom of video */}
                            <LinearGradient
                                colors={['transparent', 'rgba(0,0,0,0.7)']}
                                style={s.videoScrim}
                            />

                            {/* Duration / Upload ring — top right */}
                            <View style={[s.ringWrap, { top: insets.top + 12 }]}>
                                {uploading || uploadComplete ? (
                                    <View style={s.uploadRingInner}>
                                        <DurationRing 
                                            duration={uploadProgress * 100} 
                                            max={100} 
                                            color={uploadComplete ? '#10b981' : '#7c3aed'} 
                                            isPercentage={true}
                                        />
                                        <Text style={s.ringLabel}>{uploadComplete ? 'READY' : 'PROC'}</Text>
                                    </View>
                                ) : (
                                    <DurationRing duration={clip.duration || 0} max={MAX_DURATION} color={color} />
                                )}
                            </View>

                            {/* Over limit badge */}
                            {isOver && (
                                <View style={s.overBadge}>
                                    <Ionicons name="cut-outline" size={11} color="#fff" />
                                    <Text style={s.overBadgeText}>Longer than 30s — server will trim</Text>
                                </View>
                            )}

                            {/* Pause overlay */}
                            {!playing && (
                                <View style={s.pauseOverlay}>
                                    <Ionicons name="play-circle" size={72} color="rgba(255,255,255,0.9)" />
                                </View>
                            )}

                            {/* Back button */}
                            <TouchableOpacity
                                style={[s.backBtn, { top: insets.top + 12 }]}
                                onPress={() => { setClip(null); setStep('choose'); }}
                            >
                                <Ionicons name="chevron-back" size={22} color="#fff" />
                            </TouchableOpacity>
                        </TouchableOpacity>

                        {/* Bottom control panel */}
                        <View style={[s.panel, { paddingBottom: insets.bottom + 20 }]}>
                            {/* Metadata row */}
                            <View style={s.metaRow}>
                                <View style={s.metaLeft}>
                                    <Ionicons name="videocam" size={13} color="#a78bfa" />
                                    <Text style={s.metaText}>
                                        {fmtDuration(clip.duration)}
                                        {clip.width ? `  ·  ${clip.width}×${clip.height}` : ''}
                                        {clip.fileSize ? `  ·  ${fmtSize(clip.fileSize)}` : ''}
                                    </Text>
                                </View>
                                <View style={s.processBadge}>
                                    <Ionicons name="flash" size={11} color="#7c3aed" />
                                    <Text style={s.processBadgeText}>720p · H.264</Text>
                                </View>
                            </View>

                            {/* Trim timeline */}
                            <View style={s.timelineSection}>
                                <Text style={s.timelineLabel}>CLIP TIMELINE</Text>
                                <View style={[s.timeline, { width: TIMELINE_W }]}>
                                    {/* Track */}
                                    <LinearGradient
                                        colors={['rgba(255,255,255,0.04)', 'rgba(255,255,255,0.09)', 'rgba(255,255,255,0.04)']}
                                        style={s.timelineTrack}
                                        start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
                                    />
                                    {/* Active selection */}
                                    <Animated.View style={[s.timelineActive, {
                                        left: startX,
                                        right: Animated.subtract(TIMELINE_W, endX),
                                    }]} />
                                    {/* 30s cut-line */}
                                    {isOver && clip.duration > 0 && (
                                        <View style={[s.cutLine, { left: (30 / clip.duration) * TIMELINE_W }]}>
                                            <View style={s.cutLineBar} />
                                            <Text style={s.cutLineLabel}>30s</Text>
                                        </View>
                                    )}
                                    {/* Start handle */}
                                    <Animated.View
                                        {...startPan.panHandlers}
                                        style={[s.handle, { left: startX }]}
                                    >
                                        <View style={s.handleBar} />
                                    </Animated.View>
                                    {/* End handle */}
                                    <Animated.View
                                        {...endPan.panHandlers}
                                        style={[s.handle, { left: Animated.subtract(endX, 10) }]}
                                    >
                                        <View style={s.handleBar} />
                                    </Animated.View>
                                    {/* Time markers */}
                                    <View style={s.timeMarkers}>
                                        <Text style={s.timeMarker}>0s</Text>
                                        <Text style={s.timeMarker}>{fmtDuration((clip.duration || MAX_DURATION) / 2)}</Text>
                                        <Text style={s.timeMarker}>{fmtDuration(clip.duration || MAX_DURATION)}</Text>
                                    </View>
                                </View>
                            </View>

                            {/* CTA */}
                            {uploadComplete ? (
                                <TouchableOpacity style={s.cta} onPress={finalize} activeOpacity={0.85}>
                                    <LinearGradient
                                        colors={['#10b981', '#059669']}
                                        style={s.ctaIn}
                                        start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
                                    >
                                        <Ionicons name="arrow-forward-circle" size={20} color="#fff" />
                                        <Text style={s.ctaText}>Continue to Post</Text>
                                    </LinearGradient>
                                </TouchableOpacity>
                            ) : (
                                <TouchableOpacity 
                                    style={[s.cta, uploading && { opacity: 0.7 }]} 
                                    onPress={useClip} 
                                    activeOpacity={0.85}
                                    disabled={uploading}
                                >
                                    <LinearGradient
                                        colors={isOver ? ['#d97706', '#b45309'] : ['#7c3aed', '#5b21b6']}
                                        style={s.ctaIn}
                                        start={{ x: 0, y: 0 }} end={{ x: 1, y: 0 }}
                                    >
                                        {uploading ? (
                                            <ActivityIndicator color="#fff" style={{ marginRight: 8 }} />
                                        ) : (
                                            <Ionicons name={isOver ? 'cut' : 'checkmark-circle'} size={20} color="#fff" />
                                        )}
                                        <Text style={s.ctaText}>
                                            {uploading 
                                                ? `Processing ${Math.round(uploadProgress * 100)}%` 
                                                : isOver ? 'Use Clip — Server Will Trim to 30s' : 'Use this Clip'}
                                        </Text>
                                    </LinearGradient>
                                </TouchableOpacity>
                            )}
                        </View>
                    </View>
                )}
            </View>
        </Modal>
    );
};

// ── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
    root: { flex: 1, backgroundColor: '#000' },
    page: { flex: 1, paddingHorizontal: 20 },

    // Camera
    cameraPage: { flex: 1, backgroundColor: '#000' },
    cameraOverlay: { flex: 1, justifyContent: 'space-between', paddingHorizontal: 20 },
    recordRingWrap: { alignSelf: 'center', backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: 40, padding: 4 },
    recordCtrls: { alignItems: 'center', gap: 12 },
    recordBtn: {
        width: 80, height: 80, borderRadius: 40,
        borderWidth: 4, borderColor: '#fff',
        alignItems: 'center', justifyContent: 'center',
    },
    recordBtnActive: { borderColor: 'rgba(239,68,68,0.5)' },
    recordBtnInner: {
        width: 60, height: 60, borderRadius: 30,
        backgroundColor: '#ef4444',
    },
    recordBtnInnerActive: {
        width: 32, height: 32, borderRadius: 4,
    },
    recordStatusText: { color: '#fff', fontSize: 13, fontWeight: '700', textShadowColor: 'rgba(0,0,0,0.5)', textShadowRadius: 4 },

    // Header
    hdr: {
        flexDirection: 'row', alignItems: 'center',
        justifyContent: 'space-between', marginBottom: 20,
    },
    hdrTitle: { color: '#fff', fontSize: 17, fontWeight: '800', letterSpacing: 0.2 },
    closeBtn: {
        width: 40, height: 40, borderRadius: 20,
        backgroundColor: 'rgba(255,255,255,0.08)',
        alignItems: 'center', justifyContent: 'center',
        borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)',
    },

    // Limit pill
    limitPill: {
        flexDirection: 'row', alignItems: 'center', gap: 6,
        backgroundColor: 'rgba(245,158,11,0.1)',
        borderWidth: 1, borderColor: 'rgba(245,158,11,0.25)',
        borderRadius: 20, paddingHorizontal: 14, paddingVertical: 8,
        alignSelf: 'center', marginBottom: 28,
    },
    limitPillText: { color: 'rgba(255,255,255,0.75)', fontSize: 12, fontWeight: '600' },

    // Cards
    cardsWrap: { flex: 1, justifyContent: 'center' },
    card: {
        borderRadius: 24, overflow: 'hidden',
        borderWidth: 1, borderColor: 'rgba(255,255,255,0.08)',
    },
    cardGrad: { padding: 24, alignItems: 'center' },
    cardIconOrb: {
        width: 80, height: 80, borderRadius: 40,
        alignItems: 'center', justifyContent: 'center', marginBottom: 16,
    },
    cardTitle: { color: '#fff', fontSize: 18, fontWeight: '800', marginBottom: 6 },
    cardSub: {
        color: 'rgba(255,255,255,0.5)', fontSize: 13,
        textAlign: 'center', marginBottom: 16, lineHeight: 18,
    },
    cardTagRow: { flexDirection: 'row', gap: 8, marginBottom: 20 },
    cardTag: {
        backgroundColor: 'rgba(255,255,255,0.07)',
        borderRadius: 8, paddingHorizontal: 10, paddingVertical: 4,
        borderWidth: 1, borderColor: 'rgba(255,255,255,0.12)',
    },
    cardTagText: { color: 'rgba(255,255,255,0.6)', fontSize: 11, fontWeight: '700' },
    cardBtn: {
        flexDirection: 'row', alignItems: 'center', gap: 8,
        borderRadius: 20, paddingHorizontal: 22, paddingVertical: 11,
    },
    cardBtnText: { color: '#fff', fontSize: 14, fontWeight: '800' },

    // Preview layout
    previewRoot: { flex: 1 },
    videoWrap: { flex: 1, position: 'relative', backgroundColor: '#000' },
    video: { flex: 1 },
    videoScrim: {
        position: 'absolute', bottom: 0, left: 0, right: 0, height: 80,
    },

    // Ring
    ringWrap: {
        position: 'absolute', right: 14,
        backgroundColor: 'rgba(0,0,0,0.55)', borderRadius: 40,
        padding: 4,
    },
    uploadRingInner: { alignItems: 'center', justifyContent: 'center' },
    ringLabel: { 
        position: 'absolute', bottom: -12, 
        color: '#fff', fontSize: 8, fontWeight: '900',
        backgroundColor: '#7c3aed', paddingHorizontal: 4, borderRadius: 4
    },

    // Over-limit badge
    overBadge: {
        position: 'absolute', bottom: 14, left: 14,
        flexDirection: 'row', alignItems: 'center', gap: 5,
        backgroundColor: 'rgba(239,68,68,0.8)',
        borderRadius: 12, paddingHorizontal: 10, paddingVertical: 5,
    },
    overBadgeText: { color: '#fff', fontSize: 11, fontWeight: '700' },

    // Pause overlay
    pauseOverlay: {
        ...StyleSheet.absoluteFillObject,
        alignItems: 'center', justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.35)',
    },

    // Back button
    backBtn: {
        position: 'absolute', left: 14,
        width: 38, height: 38, borderRadius: 19,
        backgroundColor: 'rgba(0,0,0,0.55)',
        alignItems: 'center', justifyContent: 'center',
        borderWidth: 1, borderColor: 'rgba(255,255,255,0.15)',
    },

    // Bottom panel
    panel: {
        backgroundColor: '#0e0020',
        borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.06)',
        paddingHorizontal: 20, paddingTop: 16, gap: 16,
    },

    // Metadata
    metaRow: {
        flexDirection: 'row', alignItems: 'center',
        justifyContent: 'space-between',
    },
    metaLeft: { flexDirection: 'row', alignItems: 'center', gap: 6 },
    metaText: { color: 'rgba(255,255,255,0.6)', fontSize: 12, fontWeight: '600' },
    processBadge: {
        flexDirection: 'row', alignItems: 'center', gap: 4,
        backgroundColor: 'rgba(124,58,237,0.18)',
        borderRadius: 8, paddingHorizontal: 8, paddingVertical: 4,
        borderWidth: 1, borderColor: 'rgba(124,58,237,0.3)',
    },
    processBadgeText: { color: '#a78bfa', fontSize: 10, fontWeight: '800' },

    // Timeline
    timelineSection: { gap: 8 },
    timelineLabel: {
        color: 'rgba(255,255,255,0.3)', fontSize: 10,
        fontWeight: '800', letterSpacing: 1,
    },
    timeline: {
        height: 50, position: 'relative',
        justifyContent: 'center',
    },
    timelineTrack: {
        position: 'absolute', top: 10, bottom: 10, left: 0, right: 0,
        borderRadius: 8,
    },
    timelineActive: {
        position: 'absolute', top: 10, bottom: 10,
        backgroundColor: 'rgba(124,58,237,0.4)',
        borderWidth: 1.5, borderColor: '#7c3aed',
        borderRadius: 6,
    },
    cutLine: {
        position: 'absolute', top: 4, bottom: 4,
        width: 2, alignItems: 'center',
    },
    cutLineBar: { flex: 1, width: 2, backgroundColor: '#ef4444', borderRadius: 1 },
    cutLineLabel: {
        color: '#ef4444', fontSize: 9, fontWeight: '800',
        position: 'absolute', bottom: -16,
    },
    handle: {
        position: 'absolute', top: 4, bottom: 4,
        width: 10, backgroundColor: '#fff',
        borderRadius: 5, alignItems: 'center', justifyContent: 'center',
    },
    handleBar: {
        width: 2, height: 16, backgroundColor: 'rgba(0,0,0,0.4)', borderRadius: 1,
    },
    timeMarkers: {
        position: 'absolute', bottom: -18, left: 0, right: 0,
        flexDirection: 'row', justifyContent: 'space-between',
    },
    timeMarker: { color: 'rgba(255,255,255,0.3)', fontSize: 9, fontWeight: '600' },

    // CTA
    cta: { borderRadius: 28, overflow: 'hidden' },
    ctaIn: {
        flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
        gap: 10, paddingVertical: 16, paddingHorizontal: 24,
    },
    ctaText: { color: '#fff', fontSize: 15, fontWeight: '900', letterSpacing: 0.2 },
});

export default ShortClipPicker;
